// The client's side of the questionnaire: a token from the emailed link,
// or a code proved on the public page, becomes a day's session JWT held
// per-tab - a closed tab is reopened through the same link or a new
// code, so nothing longer-lived is needed.

import type {
  IntakeLinkOpenResponse,
  IntakeLinkUiResponse,
  IntakePublicStartResponse,
  IntakeQuestionnaireState,
  IntakeSubmitResponse,
} from "@duralex/shared";

const TOKEN_KEY = "intakeLinkToken";

// sessionStorage can throw or silently drop when a browser blocks site
// data, and the URL token is stripped right after the exchange - so the
// module holds the token in memory too, keeping THIS tab alive; only a
// later resume needs the emailed link again.
let memoryToken: string | null = null;

export function getIntakeLinkToken(): string | null {
  try {
    return memoryToken ?? window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return memoryToken;
  }
}

export function setIntakeLinkToken(token: string): void {
  memoryToken = token;
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* held in memory for this tab */
  }
}

export function clearIntakeLinkToken(): void {
  memoryToken = null;
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing stored */
  }
}

export class IntakeLinkApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function toJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new IntakeLinkApiError(body?.error ?? `${fallback} (HTTP ${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

function authHeaders(): Record<string, string> {
  const token = getIntakeLinkToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchIntakeUi(lang: string): Promise<IntakeLinkUiResponse> {
  const res = await fetch(`/api/intake-link/ui?lang=${encodeURIComponent(lang)}`);
  return toJson<IntakeLinkUiResponse>(res, "The page could not load");
}

export async function openIntakeLink(token: string): Promise<IntakeLinkOpenResponse> {
  const res = await fetch("/api/intake-link/open", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  return toJson<IntakeLinkOpenResponse>(res, "This link is not valid");
}

/** The public door, step one. The answer is the same for every address;
    only the firm's own mail being unproven is refused. */
export async function startPublicIntake(input: {
  email: string;
  website?: string;
  language?: string;
}): Promise<IntakePublicStartResponse> {
  const res = await fetch("/api/intake-link/public/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return toJson<IntakePublicStartResponse>(res, "A code could not be requested");
}

/** Step two: the code back, answered like an opened link. */
export async function verifyPublicIntake(input: {
  email: string;
  code: string;
  language?: string;
}): Promise<IntakeLinkOpenResponse> {
  const res = await fetch("/api/intake-link/public/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return toJson<IntakeLinkOpenResponse>(res, "That code is not valid");
}

export async function fetchIntakeState(): Promise<IntakeQuestionnaireState> {
  const res = await fetch("/api/intake-link/", { headers: authHeaders() });
  return toJson<IntakeQuestionnaireState>(res, "The questionnaire could not load");
}

export interface SaveIntakeInput {
  category?: string;
  language?: string;
  answers?: Record<string, unknown>;
}

export async function saveIntakeAnswers(input: SaveIntakeInput): Promise<IntakeQuestionnaireState> {
  const res = await fetch("/api/intake-link/answers", {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return toJson<IntakeQuestionnaireState>(res, "The answer was not saved");
}

export async function uploadIntakeDocument(input: {
  item: string;
  file: File;
}): Promise<IntakeQuestionnaireState> {
  const form = new FormData();
  form.append("item", input.item);
  form.append("file", input.file);
  const res = await fetch("/api/intake-link/documents", {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return toJson<IntakeQuestionnaireState>(res, "The file was not taken");
}

export async function deleteIntakeDocument(id: string): Promise<IntakeQuestionnaireState> {
  const res = await fetch(`/api/intake-link/documents/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return toJson<IntakeQuestionnaireState>(res, "The file was not removed");
}

export async function submitIntake(consents: string[]): Promise<IntakeSubmitResponse> {
  const res = await fetch("/api/intake-link/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ consents }),
  });
  return toJson<IntakeSubmitResponse>(res, "The form was not sent");
}
